import { Row, Col, Card, Table, Button, Modal } from "antd";

import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

// Refactored imports
import { useRobotManagement } from "../hooks/useRobotManagement";
import { organizationsService } from "../services/organizationsService";
import { robotsService } from "../services/robotsService";
import { usersService } from "../services/usersService";
import { OrganizationColorManager, BUTTON_STYLES } from "../constants";
import { createRobotColumns } from "../components/tableColumns";
import { AddRobotModal, EditRobotModal } from "../components/RobotModals";
import logger from "shared/utils/logging";

const RobotsAministrator = () => {
  const { isAuthenticated, authFetch, loading: authLoading } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  // Use custom hook for robot management (false = all robots, admin view)
  const {
    robotsData,
    robotTypes,
    loadRobots,
    loadRobotTypes,
    createRobot,
    updateRobot,
    deleteRobot,
    pagination,
  } = useRobotManagement(false); // false = all robots (admin view)

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newRobot, setNewRobot] = useState({
    robot_id: "",
    name: "",
    type_id: "",
    organizations: [],
    password: "",
    iconFile: null,
    icon_path: null,
    iconType: "preset",
  });
  const [editingRobot, setEditingRobot] = useState({
    robot_id: "",
    name: "",
    type_id: "",
    organizations: [],
    owner: "",
    icon_path: null,
    iconFile: null,
    iconType: "preset",
  });
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [organizationColorManager] = useState(
    () => new OrganizationColorManager(),
  );

  const robotsTableSource = robotsData.results || [];

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadRobots();
    }
  }, [authLoading, isAuthenticated, loadRobots]);

  const handleEditButtonClicked = (robotRecord) => {
    setSelectedRobot(robotRecord);

    setEditingRobot({
      robot_id: robotRecord.robot_id,
      name: robotRecord.name,
      type_id: robotRecord.robot_type?.type_id,
      organizations: robotRecord.organizations?.map((org) => org.org_id) || [],
      password: "",
      api_key: robotRecord.api_key || "",
      owner: robotRecord.created_user?.username || null, // Assuming created_user is the owner
      icon_path: robotRecord.icon_path,
      iconFile: null,
      iconType: "preset",
    });

    setIsModalVisible(true);
  };

  const areSetsIdentical = (setA, setB) => {
    if (setA.size !== setB.size) return false;
    for (let item of setA) {
      if (!setB.has(item)) {
        return false;
      }
    }
    return true;
  };

  const handleEditModalOk = async () => {
    const editingData = { ...editingRobot };

    delete editingData.api_key;
    delete editingData.iconFile; // Don't send iconFile
    delete editingData.iconType; // Don't send iconType

    if (editingData.password === "") {
      delete editingData.password;
    }
    // comparing the editingRobot.organizations with the selectedRobot.organizations to check if they are different
    const newOrgsIdsSet = new Set(editingData.organizations);
    const oldOrgsIdsSet = new Set(
      selectedRobot.organizations?.map((org) => org.org_id),
    );
    // check if newOrgsIdsSet and oldOrgsIdsSet are different
    if (areSetsIdentical(newOrgsIdsSet, oldOrgsIdsSet)) {
      delete editingData.organizations;
    }

    if (!selectedRobot.robot_type?.type_id && !editingData.type_id) {
      delete editingData.type_id;
    } else if (editingData.type_id === selectedRobot.robot_type?.type_id) {
      delete editingData.type_id;
    }

    if (editingData.name === selectedRobot.name) {
      delete editingData.name;
    }
    if (editingData.robot_id === selectedRobot.robot_id) {
      delete editingData.robot_id;
    }

    logger.log("Selected robot created user:", selectedRobot.created_user);
    logger.log("Editing robot owner:", editingData.owner);

    if (!selectedRobot.created_user?.username && !editingData.owner) {
      delete editingData.owner;
    } else if (editingData.owner === selectedRobot.created_user?.username) {
      delete editingData.owner;
    }

    if (
      !editingData?.icon_path ||
      editingData?.icon_path === selectedRobot?.icon_path
    ) {
      delete editingData.icon_path; // Don't send icon_path directly
    }

    const hasIconUpload = editingRobot.iconFile !== null;
    const hasDataChanges = Object.keys(editingData).length > 0;

    logger.log("Editing data to be sent:", editingData);
    if (!hasDataChanges && !hasIconUpload) {
      notifyError("No changes made to update.");
      setIsModalVisible(false);
      return;
    }

    try {
      // Update robot data if there are changes
      if (hasDataChanges) {
        await updateRobot(selectedRobot.robot_id, editingData);
      }

      // Upload icon if user selected one
      if (hasIconUpload) {
        await robotsService.uploadIcon(
          authFetch,
          selectedRobot.robot_id,
          editingRobot.iconFile,
        );
      }

      setIsModalVisible(false);
      notifySuccess("Robot updated successfully.");
      loadRobots(); // Reload robots to show updated icon
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleEditModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddButtonClick = () => {
    setIsAddModalVisible(true);
  };

  const handleAddModalOk = async () => {
    try {
      const robotData = { ...newRobot };
      delete robotData.iconFile; // Don't send iconFile in create request
      delete robotData.iconType; // Don't send iconType in create request

      const createdRobot = await createRobot(robotData);

      // Upload icon if user selected one
      if (newRobot.iconFile && createdRobot) {
        await robotsService.uploadIcon(
          authFetch,
          robotData.robot_id,
          newRobot.iconFile,
        );
      }

      setIsAddModalVisible(false);
      setNewRobot({
        robot_id: "",
        name: "",
        type_id: "",
        organizations: [],
        password: "",
        iconFile: null,
        icon_path: null,
        iconType: "preset",
      });
      notifySuccess("New robot added successfully.");
      loadRobots(); // Reload robots to show new robot with icon
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      notifyError("Failed to add new robot: " + errorMessage);
    }
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
  };

  const handleDeleteButtonClicked = (robotId) => {
    Modal.confirm({
      title: `Are you sure you want to delete this robot "${robotId}"?`,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteRobot(robotId);
          notifySuccess("Robot deleted successfully.");
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          notifyError("Failed to delete robot: " + errorMessage);
        }
      },
    });
  };

  // Create table columns using shared factory
  const columns = createRobotColumns(organizationColorManager, (key) => key, {
    showOwner: true,
    showActions: true,
    onEdit: handleEditButtonClicked,
    onDelete: handleDeleteButtonClicked,
  });

  useEffect(() => {
    const loadAdditionalData = async () => {
      try {
        const [orgsResponse, usersResponse] = await Promise.all([
          organizationsService.getAll(authFetch),
          usersService.getAll(authFetch),
        ]);
        const orgsData = orgsResponse?.data;
        const usersData = usersResponse?.data;
        setOrganizations(orgsData?.results || []);
        setUsers(usersData?.results || []);
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;
        logger.error("Error fetching additional data:", error);
        notifyError("Failed to fetch organizations/users: " + errorMessage);
      }
    };

    if (!authLoading && isAuthenticated && typeof authFetch === "function") {
      loadAdditionalData();
      loadRobotTypes();
    }
  }, [authLoading, isAuthenticated, authFetch, notifyError, loadRobotTypes]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Robots Administrations"
              extra={
                <>
                  {/* <Radio.Group onChange={onChange} defaultValue="a">
                    <Radio.Button value="a">All</Radio.Button>
                    <Radio.Button value="b">ONLINE</Radio.Button>
                  </Radio.Group> */}
                  <Button
                    // type="primary"
                    icon={<PlusOutlined />}
                    style={BUTTON_STYLES.add}
                    onClick={handleAddButtonClick}
                  >
                    New Robot
                  </Button>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={robotsTableSource}
                  rowKey="robot_id"
                  pagination={{
                    current: pagination.page,
                    pageSize: pagination.per_page,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (page, pageSize) => {
                      loadRobots(page, pageSize);
                    },
                  }}
                  className="ant-border-space"
                  onRow={(record) => ({
                    onDoubleClick: () => handleEditButtonClicked(record),
                  })}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {/* Edit robot modal */}
      <EditRobotModal
        visible={isModalVisible}
        robot={editingRobot}
        robotTypes={robotTypes}
        organizations={organizations}
        users={users}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        onChange={(updates) => setEditingRobot({ ...editingRobot, ...updates })}
        showOwner={true}
      />

      {/* Add robot modal */}
      <AddRobotModal
        visible={isAddModalVisible}
        robot={newRobot}
        robotTypes={robotTypes}
        organizations={organizations}
        onOk={handleAddModalOk}
        onCancel={handleAddModalCancel}
        onChange={(updates) => setNewRobot({ ...newRobot, ...updates })}
      />
    </>
  );
};

export default RobotsAministrator;
